import { CSSProperties } from 'react';
import useWidthBreakpoint from '../../../../../../hooks/useWidthBreakpoint';
import { useResultData } from '../../context';
import { txColor } from '../../txHelpers';
import { tokens } from '../../tokens';
import { pick } from '../../responsive';
import ExpandedRailInfo from '../shared/ExpandedRailInfo';
import ChartStack, { RsiDescriptor } from '../charts/ChartStack';
import { findMrcEpisodeForTx } from '../../derived/mrcEpisodes';
import MrcPhaseFlow from '../shared/MrcPhaseFlow';

interface Props {
  txId: number;
  isFirst?: boolean;
  onCollapse?: () => void;
  onToggleMode?: () => void;
}

const ExpandedRowMrc = ({ txId, isFirst, onCollapse, onToggleMode }: Props) => {
  const displayData = useResultData();
  const { breakpoint } = useWidthBreakpoint();
  const tx = displayData.deepByTxId[txId];
  const mrcPhases = displayData.mrcByTxId[txId] ?? [];
  if (!tx) return null;
  const accentColor = txColor(tx.type);

  // Match the closed row's top border exactly so toggling the deep dive never
  // nudges the content down by a border width.
  const topBorder = isFirst ? '1px solid rgba(255,255,255,0.22)' : 'none';

  const padding = pick(breakpoint, {
    sm: '0',
    md: '0',
    lg: '0',
  });

  const container: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      display: 'flex',
      flexDirection: 'column',
      borderTop: topBorder,
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
    md: {
      display: 'flex',
      alignItems: 'flex-start',
      borderTop: topBorder,
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
    lg: {
      display: 'flex',
      alignItems: 'flex-start',
      borderTop: topBorder,
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
  });

  const accent: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { height: 3, background: accentColor, flexShrink: 0 },
    md: {
      width: 3,
      background: accentColor,
      flexShrink: 0,
      alignSelf: 'stretch',
    },
    lg: {
      width: 3,
      background: accentColor,
      flexShrink: 0,
      alignSelf: 'stretch',
    },
  });

  const rail: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      width: '100%',
      background: '#000',
      padding: '16px 18px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    md: {
      width: 360,
      flexShrink: 0,
      background: '#000',
      padding: '20px 28px 24px 26px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      borderRight: '1px solid rgba(255,255,255,0.08)',
      minHeight: 0,
    },
    lg: {
      width: 360,
      flexShrink: 0,
      background: '#000',
      padding: '20px 28px 24px 26px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      borderRight: '1px solid rgba(255,255,255,0.08)',
      minHeight: 0,
    },
  });

  const rightSide: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column' },
    md: { flex: 1, display: 'flex', flexDirection: 'column' },
    lg: { flex: 1, display: 'flex', flexDirection: 'column' },
  });

  const chartHeight = pick(breakpoint, { sm: 220, md: 320, lg: 420 });

  // Only mount MRC RSI panels for timeframes that actually have values.
  const chartRsis: RsiDescriptor[] = [
    displayData.raw.mrcRsiHigherTValues?.length && {
      key: 'mrcHigher',
      side: 'mrcHigher',
      label: `HIGHER T MRC RSI · ${displayData.timeframes.higherT}`,
    },
    displayData.raw.mrcRsiShorterTValues?.length && {
      key: 'mrcShorter',
      side: 'mrcShorter',
      label: `SHORTER T MRC RSI · ${displayData.timeframes.shorterT}`,
    },
    displayData.raw.mrcRsiMultiTValues?.length && {
      key: 'mrcMulti',
      side: 'mrcMulti',
      label: `MULTI T MRC RSI · ${displayData.timeframes.multiT}`,
    },
  ].filter(Boolean) as RsiDescriptor[];

  // The MRC episode that resolved into this transaction — drives the
  // deep-dive zoom + phase lines on the candle chart.
  const mrcEpisode = findMrcEpisodeForTx(displayData.raw, txId) ?? undefined;

  return (
    <div
      style={{
        background: tokens.backgroundBase,
        padding,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <div style={container}>
        <div style={accent} />

        <div style={rail}>
          <ExpandedRailInfo tx={tx} onCollapse={onCollapse} />

          {onToggleMode && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onToggleMode}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(240,132,22,0.12)',
                  color: tokens.amber,
                  border: `1px solid ${tokens.amber}66`,
                  borderRadius: 3,
                  fontFamily: tokens.fontMono,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: tokens.amber,
                  }}
                />
                MRC DEEP DIVE · ON
              </button>
            </div>
          )}

          <MrcPhaseFlow phases={mrcPhases} />
        </div>

        <div style={rightSide}>
          <div style={{ position: 'relative' }}>
            <ChartStack
              txId={txId}
              mainHeight={chartHeight}
              rsis={chartRsis}
              mrcEpisode={mrcEpisode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpandedRowMrc;
